import './styles.css';
import { createMap } from './map/createMap';
import { RouteLayer } from './map/routeLayer';
import { DestinationPin } from './map/destination';
import { onLongPress } from './map/longPress';
import { isOnHighway, nearestLocalRoad } from './map/roads';
import { confirmDialog } from './ui/confirm';
import { Popup } from 'maplibre-gl';
import { ArrowVehicle, type VehicleView } from './map/vehicle';
import { CameraController } from './map/camera';
import { Navigator, roundDistance, type HighwayState, type NavState } from './nav/navigator';
import { distance } from './nav/geo';
import { ReroutePolicy } from './nav/reroute';
import { parseJunctions, type Junction, type JunctionRow, type LaneGuide } from './nav/lanes';
import { COMPASS, laneIcon, maneuverIcon } from './ui/icons';
import { isPhoneLandscape, watchLayout } from './ui/layout';
import { buildHighwayPlan, enrichSteps, parseFacilities, type Facility, type FacilityRow, type HighwayPlan } from './nav/highway';
import {
  GeolocationSource,
  GpxReplaySource,
  geolocationPermission,
  RouteSimSource,
  parseGpx,
  type PositionSample,
  type PositionSource,
} from './sensors/position';
import { WakeLockKeeper } from './sensors/wakeLock';
import { Compass } from './sensors/compass';
import { Speaker } from './voice/speech';
import { fetchRoute, geocode, ApiClientError } from './api/client';
import { Diagnostics } from './ui/diagnostics';
import { showDisclaimer } from './ui/disclaimer';
import { store } from './ui/storage';
import { ROUTE_TYPES, type LngLat, type ManeuverType, type Route, type RouteType } from './shared/types';

/* ------------------------------------------------------------------ */
/* 準備                                                                */
/* ------------------------------------------------------------------ */

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;

const map = createMap($('map'));
// 端末の向きでレイアウトを切り替える（キーボードで高さが縮んでも横向き扱いにしない）。地図の大きさも合わせ直す
watchLayout(() => map.resize());
const routeLayer = new RouteLayer(map);
const destPin = new DestinationPin(map);
const camera = new CameraController(map);
const arrow = new ArrowVehicle(map);
let vehicle: VehicleView = arrow;
let threeLayer: (VehicleView & { id: string }) | null = null;

const wake = new WakeLockKeeper();
const compass = new Compass();
const speaker = new Speaker();
speaker.chime = store.get('navi.chime') !== '0';

let source: PositionSource | null = null;
let lastSample: PositionSample | null = null;
let destination: LngLat | null = null;
/** 今のルートが GPX 再生用の仮ルート（トラックそのもの）か */
let trackRoute = false;
let currentRoute: Route | null = null;
let currentAttribution: string | undefined;
/** ルートを選んでいる途中の目的地（「案内開始」で destination になる） */
let pending: LngLat | null = null;
/** テスト走行の倍速（診断パネルで切り替える） */
const SIM_FACTORS = [1, 2, 4, 8];
let simFactor = 4;
const reroute = new ReroutePolicy();
const REROUTE_SKIP: Record<string, string> = { stopped: '停車中', inaccurate: 'GPS の精度が低い', waiting: '間隔を空けている', blocked: '上限に達している' };
/** 選んだルートの種類（再探索でも同じ種類で探す） */
let routeType: RouteType = 'fastest';

const diag = new Diagnostics(document.body, [
  { label: '音声テスト', run: () => speaker.say('音声案内のテストです。300メートル先、右方向です。', { interrupt: true }) },
  { label: 'スリープ防止 再取得', run: () => void wake.enable() },
  {
    label: () => `案内音 ${speaker.chime ? 'オン' : 'オフ'}`,
    run: () => {
      speaker.chime = !speaker.chime;
      store.set('navi.chime', speaker.chime ? '1' : '0');
    },
  },
  { label: 'GPXファイル読込', run: () => $('gpx-file').click() },
  {
    label: () => `テスト走行 ${simFactor}倍速`,
    run: () => {
      simFactor = SIM_FACTORS[(SIM_FACTORS.indexOf(simFactor) + 1) % SIM_FACTORS.length];
      if (source instanceof RouteSimSource) source.setSpeedFactor(simFactor);
    },
  },
]);

/* ------------------------------------------------------------------ */
/* ナビゲーション                                                      */
/* ------------------------------------------------------------------ */

const nav = new Navigator({
  onState: (s) => {
    vehicle.update(s.position, s.bearing);
    camera.follow(s.position, s.bearing, s.raw.speed, !!s.highway);
    renderHud(s);
    renderHighway(s.highway);
    reroute.observe(!s.offRoute && s.offset < 35);
    diag.set('offset', `${s.offset.toFixed(0)} m`);
    diag.set('compass', compass.heading == null ? compass.status : `${compass.heading.toFixed(0)}°`);
  },
  onOffRoute: (pos) => {
    if (!destination || !lastSample) return;
    // 経路探索の回数を節約する（止まっている・精度が悪い・続けて外れている・上限のときはしない）
    const d = reroute.decide(lastSample);
    if (!d.ok) {
      diag.log(`ルート逸脱を検知（再探索しない: ${REROUTE_SKIP[d.reason]}）`);
      return;
    }
    diag.log('ルート逸脱を検知');
    reroute.rerouted();
    speaker.say('ルートを再検索します。');
    void requestRoute(pos, destination, routeType);
  },
  onAnnounce: (text) => {
    diag.log(`案内: ${text}`);
    speaker.say(text);
  },
});

function handleSample(s: PositionSample) {
  lastSample = s;
  diag.recordSample(s);
  nav.update(s);
}

function startSource(next: PositionSource) {
  source?.stop();
  // GPX 再生用の仮ルートは GPS 走行には関係ないので消す
  if (next.kind !== 'replay' && trackRoute) applyRoute(null);
  source = next;
  source.start(handleSample, (e, fatal) => {
    toast(e, fatal ? 15000 : 4000);
    diag.log(e);
    if (fatal) stopSource();
  });
  camera.setMode(camera.defaultMode());
  updateButtons();
}

function stopSource() {
  source?.stop();
  source = null;
  updateButtons();
}

/* 高速道路の施設データ（OSM から作った静的ファイル）。高速を通るルートのときだけ読み込む */
let facilities: Promise<Facility[]> | null = null;
function loadFacilities(): Promise<Facility[]> {
  facilities ??= fetch('/data/highway-facilities.json')
    .then((r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json() as Promise<{ items: FacilityRow[] }>;
    })
    .then((d) => parseFacilities(d.items))
    .catch((e) => {
      facilities = null; // 次のルートで取り直す
      throw e;
    });
  return facilities;
}

/* 分岐点の車線データ（同じく高速を通るときだけ。無くても施設リストなどは動く） */
let junctions: Promise<Map<string, Junction>> | null = null;
function loadJunctions(): Promise<Map<string, Junction> | undefined> {
  junctions ??= fetch('/data/highway-lanes.json')
    .then((r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json() as Promise<{ items: JunctionRow[] }>;
    })
    .then((d) => parseJunctions(d.items));
  return junctions.catch((e) => {
    junctions = null;
    diag.log(`車線データを読めませんでした: ${e instanceof Error ? e.message : e}`);
    return undefined;
  });
}

/** 高速を通るルートなら、施設を照合して案内文に IC・JCT の名前と車線案内を入れる。失敗しても普通のナビとして続ける */
async function withHighwayPlan(route: Route): Promise<{ route: Route; plan: HighwayPlan | null }> {
  if (!route.highways?.length) return { route, plan: null };
  try {
    const [fac, jn] = await Promise.all([loadFacilities(), loadJunctions()]);
    const plan = buildHighwayPlan(route, fac, jn);
    diag.log(`高速区間 ${plan.sections.length} ／ 施設 ${plan.facilities.length} 件 ／ 車線案内 ${plan.guides.size} 件`);
    return { route: { ...route, steps: enrichSteps(route, plan) }, plan };
  } catch (e) {
    diag.log(`高速道路の施設データを読めませんでした: ${e instanceof Error ? e.message : e}`);
    return { route, plan: null };
  }
}

interface RouteResult {
  type: RouteType;
  route: Route;
  plan: HighwayPlan | null;
  attribution?: string;
}

async function fetchCandidate(from: LngLat, to: LngLat, type: RouteType): Promise<RouteResult> {
  const res = await fetchRoute(from, to, type);
  const { route, plan } = await withHighwayPlan(res.route);
  diag.log(`ルート取得 (${res.provider}・${ROUTE_LABEL[type]}) ${roundDistance(res.route.distance)}`);
  return { type, route, plan, attribution: res.attribution };
}

function routeErrorMessage(e: unknown): string {
  if (e instanceof ApiClientError) {
    if (e.code === 'quota_exceeded') return '本日の経路検索の上限に達しました。明日またお試しください（地図・検索・案内中のルートは使えます）';
    if (e.code === 'rate_limited') return '経路検索が混み合っています。少し待ってからお試しください';
    if (e.code === 'no_route') return e.message;
  }
  // fetch 自体の失敗（圏外・サーバー停止など）
  if (e instanceof TypeError) return 'サーバーに接続できません。通信状態を確認してください';
  return `経路検索に失敗しました: ${e instanceof Error ? e.message : e}`;
}

/** 経路探索が失敗したとき。上限なら、しばらく再探索しない */
function onRouteError(e: unknown): string {
  if (e instanceof ApiClientError && e.code === 'quota_exceeded') reroute.block();
  const msg = routeErrorMessage(e);
  toast(msg, 8000);
  diag.log(msg);
  return msg;
}

/** 再探索（ルートを外れたとき）。選んだ種類のまま、確認なしで案内を続ける */
async function requestRoute(from: LngLat, to: LngLat, type: RouteType) {
  try {
    const c = await fetchCandidate(from, to, type);
    applyRoute(c.route, { attribution: c.attribution, plan: c.plan });
  } catch (e) {
    onRouteError(e);
  }
}

/* ------------------------------------------------------------------ */
/* ルートの選択（目的地を決めたら、案内を始める前に選んでもらう）          */
/* ------------------------------------------------------------------ */

const ROUTE_LABEL: Record<RouteType, string> = { fastest: '高速優先', nohighway: '一般道優先', shortest: '最短距離' };

/**
 * 候補は種類ごと。経路探索の回数を節約するため、最初は前回選んだ種類の1本だけ取り、
 * ほかはタップしたときに取る
 */
interface Candidate {
  type: RouteType;
  state: 'idle' | 'loading' | 'done' | 'error';
  result?: RouteResult;
  error?: string;
}
let candidates: Candidate[] = [];
let chosen = 0;
let previewFrom: LngLat | null = null;

async function previewRoutes(from: LngLat, to: LngLat) {
  $('routes').hidden = false;
  previewFrom = from;
  candidates = ROUTE_TYPES.map((type) => ({ type, state: 'idle' }));
  chosen = Math.max(0, ROUTE_TYPES.indexOf(routeType));
  renderRoutes();
  await loadCandidate(chosen, to);
}

async function loadCandidate(i: number, to = pending) {
  const c = candidates[i];
  if (!c || !previewFrom || !to || c.state === 'loading' || c.state === 'done') return;
  const list = candidates;
  c.state = 'loading';
  renderRoutes();
  try {
    c.result = await fetchCandidate(previewFrom, to, c.type);
    c.state = 'done';
  } catch (e) {
    c.state = 'error';
    c.error = onRouteError(e);
  }
  if (list !== candidates || pending !== to) return; // 待っている間に別の目的地を選んだ／キャンセルした
  renderRoutes();
  if (i === chosen && c.state === 'done') fitRoutes();
}

/** 高速を走る距離 [m] */
function highwayMeters(r: Route): number {
  let d = 0;
  for (const [a, b] of r.highways ?? []) for (let i = a; i < b && i < r.geometry.length - 1; i++) d += distance(r.geometry[i], r.geometry[i + 1]);
  return d;
}

const duration = (sec: number) => {
  const m = Math.round(sec / 60);
  return m >= 60 ? `${Math.floor(m / 60)}時間${m % 60}分` : `${m}分`;
};

function renderRoutes() {
  const list = $('routes-list');
  list.innerHTML = '';
  const done = candidates.filter((c) => c.state === 'done');
  candidates.forEach((c, i) => {
    const li = document.createElement('li');
    li.className = 'route-card' + (i === chosen ? ' sel' : '') + (c.state === 'done' ? '' : ' pending');
    li.setAttribute('role', 'radio');
    li.setAttribute('aria-checked', String(i === chosen));
    li.innerHTML = `
      <div class="route-title"></div>
      <div class="route-time"></div>
      <div class="route-sub"></div>
      <div class="route-arrive"></div>`;
    const r = c.result?.route;
    let title = ROUTE_LABEL[c.type];
    if (r) {
      // 同じルートになった種類があれば分かるようにする（近距離では3種類とも同じになりやすい）
      const same = done.find((o) => o !== c && candidates.indexOf(o) < i && Math.abs(o.result!.route.distance - r.distance) < 30 && Math.abs(o.result!.route.duration - r.duration) < 30);
      if (same) title += `（${ROUTE_LABEL[same.type]}と同じ）`;
      const hw = highwayMeters(r);
      li.querySelector('.route-time')!.textContent = duration(r.duration);
      li.querySelector('.route-sub')!.textContent = `${km(r.distance)} ・ ${hw > 0 ? `高速 ${km(hw)}` : '高速なし'}`;
      li.querySelector('.route-arrive')!.textContent = `${new Date(Date.now() + r.duration * 1000).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })} 着`;
    } else {
      li.querySelector('.route-sub')!.textContent =
        c.state === 'loading' ? '探しています…' : c.state === 'error' ? `${c.error ?? '探せませんでした'}（タップで再試行）` : 'タップして探す';
    }
    li.querySelector('.route-title')!.textContent = title;
    li.addEventListener('click', () => {
      chosen = i;
      if (c.state === 'error') c.state = 'idle';
      renderRoutes();
      if (c.state === 'idle') void loadCandidate(i);
      else if (c.state === 'done') fitRoutes();
    });
    list.append(li);
  });
  const sel = candidates[chosen]?.result;
  routeLayer.set(
    sel?.route ?? null,
    sel?.attribution,
    done.filter((o) => o !== candidates[chosen]).map((o) => o.result!.route),
  );
  $<HTMLButtonElement>('routes-start').disabled = !sel;
}

/** 候補がすべて見えるように地図を合わせる（選ぶ画面と下部パネルの分は空ける） */
function fitRoutes() {
  camera.setMode('free');
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const c of candidates)
    for (const [x, y] of c.result?.route.geometry ?? []) {
      minX = Math.min(minX, x); maxX = Math.max(maxX, x);
      minY = Math.min(minY, y); maxY = Math.max(maxY, y);
    }
  // スマホ横向き（地図が左、ナビの表示が右）のときは、地図の上に重なるものが無い
  const split = isPhoneLandscape();
  const top = split ? 30 : $('routes').getBoundingClientRect().bottom + 20;
  // 下部パネルと、その上の地図の帰属表示の分を空ける（帰属表示の後ろにルートを隠さない）
  const lowest = Math.min(
    document.querySelector('.toolbar')!.getBoundingClientRect().top,
    document.querySelector('#map .maplibregl-ctrl-bottom-right')?.getBoundingClientRect().top ?? Infinity,
  );
  const bottom = split ? 30 : window.innerHeight - lowest + 20;
  // 追従カメラが付けた余白（自車を下寄りにするための上余白）が残っていると、下の padding に上乗せされて大きく引きすぎるので戻す
  map.setPadding({ top: 0, bottom: 0, left: 0, right: 0 });
  map.fitBounds([[minX, minY], [maxX, maxY]], { padding: { top, bottom, left: 40, right: 40 }, pitch: 0, bearing: 0, duration: 800 });
}

function closeRoutes() {
  $('routes').hidden = true;
  candidates = [];
  destPin.setDraggable(false);
}

function startGuidance() {
  const c = candidates[chosen]?.result;
  if (!c) return;
  routeType = c.type;
  reroute.reset();
  destination = pending;
  pending = null;
  closeRoutes();
  applyRoute(c.route, { attribution: c.attribution, plan: c.plan });
  camera.setMode(camera.defaultMode());
  if (lastSample) nav.update(lastSample);
  diag.log(`案内開始（${ROUTE_LABEL[c.type]}）`);
  if (!source) toast('「GPS」で走行開始、または「テスト」でこのルートをテスト走行できます');
}

/** 選ぶのをやめる。案内中だったなら、そのルートの案内に戻る */
function cancelRoutes() {
  closeRoutes();
  // 案内中でなければ、ピンは候補として残す（ずらしてもう一度「ここに行く」できる）
  if (!destination && pending) {
    candidate = pending;
    destPin.setDraggable(true);
  } else destPin.set(destination);
  pending = null;
  routeLayer.set(currentRoute, currentAttribution);
  camera.setMode(camera.defaultMode());
  if (lastSample) nav.update(lastSample);
}

function applyRoute(route: Route | null, opts: { fromTrack?: boolean; attribution?: string; plan?: HighwayPlan | null } = {}) {
  trackRoute = !!route && !!opts.fromTrack;
  currentRoute = route;
  // 速度・残り距離・到着時刻は、案内を始めてから出す
  document.body.classList.toggle('guiding', !!route);
  currentAttribution = route ? opts.attribution : undefined;
  nav.setRoute(route, opts.plan ?? null);
  if (!route) {
    renderHighway(undefined);
    renderLanes(undefined);
  }
  routeLayer.set(route, opts.attribution);
  $('next').hidden = !route;
  // 止まっていると次の位置がしばらく来ないので、新しいルートをすぐ表示に反映する
  if (route && lastSample) nav.update(lastSample);
  if (!route) {
    $('remaining').textContent = '—';
    $('eta').textContent = '—';
  }
}

/** 目的地を決めたら、ルートを選ぶ画面を出す（案内は「案内開始」を押してから） */
async function setDestination(to: LngLat) {
  pending = to;
  destPin.set(to);
  destPin.setDraggable(true);
  const from = lastSample?.position ?? (map.getCenter().toArray() as LngLat);
  await previewRoutes(from, to);
}

/** GPX トラックそのものをルートとして使う（API を使わずにスナップ等を検証する用） */
function routeFromTrack(points: LngLat[]): Route {
  return {
    geometry: points,
    distance: 0,
    duration: 0,
    steps: [
      { instruction: 'ルートに沿って進んでください', maneuver: 'depart', distance: 0, duration: 0, startIndex: 0 },
      { instruction: '目的地です', maneuver: 'arrive', distance: 0, duration: 0, startIndex: points.length - 1 },
    ],
  };
}

async function startReplay(xml: string) {
  const pts = parseGpx(xml);
  if (!destination) applyRoute(routeFromTrack(pts.map((p) => p.position)), { fromTrack: true });
  startSource(new GpxReplaySource(pts, { speedFactor: 2, fallbackSpeed: 11, intervalMs: 1000, jitter: 6 }));
  diag.log(`GPX 再生開始（${pts.length} 点・2倍速・ブレ6m）`);
}

/* ------------------------------------------------------------------ */
/* 3D（Three.js は必要になった時だけ読み込む）                          */
/* ------------------------------------------------------------------ */

async function toggle3D() {
  if (threeLayer) {
    map.removeLayer(threeLayer.id);
    threeLayer = null;
    vehicle = arrow;
    arrow.setVisible(true);
  } else {
    const { ThreeVehicleLayer } = await import('./layers/threeLayer');
    const layer = new ThreeVehicleLayer();
    map.addLayer(layer);
    threeLayer = layer;
    vehicle = layer;
    arrow.setVisible(false);
    if (lastSample) nav.update(lastSample);
  }
  updateButtons();
}

/* ------------------------------------------------------------------ */
/* UI                                                                  */
/* ------------------------------------------------------------------ */

/* 案内バーの矢印。変わったときだけ差し替える */
let iconKey = '';
function setNextIcon(key: ManeuverType | 'arrived' | 'off') {
  if (key === iconKey) return;
  iconKey = key;
  $('next-icon').innerHTML = key === 'off' ? '' : maneuverIcon(key);
}

/* 車線案内。分岐が変わったときだけ車線を描き直す */
let lanesShown: LaneGuide | undefined;
function renderLanes(g: LaneGuide | undefined) {
  $('lanes').hidden = !g;
  if (!g || g === lanesShown) return;
  lanesShown = g;
  $('lanes-row').innerHTML = g.lanes.map((l) => `<div class="lane${l.recommended ? ' rec' : ''}">${laneIcon(l.arrows)}</div>`).join('');
  $('lanes-toward').textContent = g.toward ? `${g.toward} 方面` : '';
}

function renderHud(s: NavState) {
  $('speed').textContent = s.raw.speed == null ? '—' : String(Math.round(s.raw.speed * 3.6));
  renderLanes(s.lanes);
  if (s.offRoute) {
    // 古いルートの残り距離や案内は当てにならないので出さない
    setNextIcon('off');
    $('next').hidden = false;
    $('next-dist').textContent = '—';
    $('next-text').textContent = 'ルートを外れています';
    $('remaining').textContent = '—';
    $('eta').textContent = '—';
    return;
  }
  if (s.remaining != null) {
    $('remaining').textContent = roundDistance(s.remaining).replace('メートル', 'm').replace('キロ', 'km');
    // 到着予想はプロバイダの所要時間から（GPX の仮ルートなど所要時間が無いときは今の速度から）
    const sec = s.remainingTime || s.remaining / (Math.max(20, (s.raw.speed ?? 0) * 3.6) / 3.6);
    $('eta').textContent = new Date(Date.now() + sec * 1000).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' });
  }
  if (s.nextStep) {
    setNextIcon(s.arrived ? 'arrived' : s.nextStep.maneuver);
    $('next').hidden = false;
    $('next-dist').textContent = s.arrived ? '到着' : roundDistance(s.distanceToNextStep ?? 0);
    $('next-text').textContent = s.arrived ? '目的地に到着しました' : s.nextStep.instruction;
  }
}

/* 高速道路モードの施設リスト。行は使い回して、毎秒の更新で DOM を作り直さない */
const hwRows: HTMLLIElement[] = [];
const KIND_LABEL = { IC: 'IC', JCT: 'JCT', SA: 'SA', PA: 'PA', TOLL: '料金所' } as const;
const km = (m: number) => (m >= 10_000 ? `${Math.round(m / 1000)}km` : `${(m / 1000).toFixed(1)}km`);
const hhmm = (t: number) => new Date(t).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' });
const hwCells = (el: HTMLElement) =>
  el.querySelector('.hw-kind') ? el : ((el.innerHTML = '<span class="hw-kind"></span><span class="hw-name"></span><span class="hw-dist"></span><span class="hw-eta"></span>'), el);

function renderHighway(h: HighwayState | undefined) {
  document.body.classList.toggle('highway', !!h);
  $('hw').hidden = !h;
  if (!h) return;
  const list = $('hw-list');
  // 近いものほど下に並べる
  const items = [...h.upcoming].reverse();
  while (hwRows.length < items.length) {
    const li = document.createElement('li');
    li.className = 'hw-row';
    hwRows.push(li);
  }
  hwRows.forEach((li, i) => {
    const f = items[i];
    if (!f) return li.remove();
    hwCells(li);
    li.dataset.kind = f.kind;
    li.querySelector('.hw-kind')!.textContent = KIND_LABEL[f.kind];
    li.querySelector('.hw-name')!.textContent = f.name;
    li.querySelector('.hw-dist')!.textContent = km(f.distance);
    li.querySelector('.hw-eta')!.textContent = hhmm(f.eta);
    if (li.parentElement !== list || list.children[i] !== li) list.insertBefore(li, list.children[i] ?? null);
  });
  const exit = hwCells($('hw-exit'));
  exit.querySelector('.hw-kind')!.textContent = '出口';
  exit.querySelector('.hw-name')!.textContent = h.exit.name ?? '高速道路の出口';
  exit.querySelector('.hw-dist')!.textContent = km(h.exit.distance);
  exit.querySelector('.hw-eta')!.textContent = hhmm(h.exit.eta);
}

function updateButtons() {
  $('btn-gps').classList.toggle('on', source?.kind === 'gps');
  $('btn-replay').classList.toggle('on', source?.kind === 'replay' || source?.kind === 'sim');
  $('btn-3d').classList.toggle('on', !!threeLayer);
  $('btn-camera').textContent = { chase: '追従', overview: '俯瞰', free: '自由' }[camera.mode];
}

let toastTimer = 0;
function toast(msg: string, ms = 4000) {
  const t = $('toast');
  t.textContent = msg;
  t.hidden = false;
  clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => (t.hidden = true), ms);
}

camera.onChange(updateButtons);
// スリープ防止が使えないときは、原因と対処を一度だけ知らせる（Android の省電力モードでは拒否される。実機で発生）
let wakeWarned = false;
wake.onChange((s, d) => {
  if ((s === 'error' || s === 'unsupported') && !wakeWarned) {
    wakeWarned = true;
    toast(
      s === 'unsupported'
        ? 'このブラウザは画面のスリープ防止に対応していません。端末の画面の自動消灯の時間を長くしてください'
        : '画面のスリープ防止が使えません。省電力モード（バッテリーセーバー）がオンだと使えないので、オフにしてください',
      12_000,
    );
  }
  if (s === 'active') wakeWarned = false;
  diag.set('wake', s + (d ? ` (${d})` : ''));
  diag.log(`スリープ防止: ${s}${d ? `（${d}）` : ''}`);
});

$('btn-gps').addEventListener('click', () => (source?.kind === 'gps' ? stopSource() : startSource(new GeolocationSource())));
$('btn-replay').addEventListener('click', async () => {
  if (source?.kind === 'replay' || source?.kind === 'sim') return stopSource();
  // ルートがあれば、そのルートをテスト走行する（一般道 40km/h・高速 100km/h を倍速で）
  if (currentRoute && !trackRoute) {
    startSource(new RouteSimSource(currentRoute.geometry, currentRoute.highways, { speedFactor: simFactor }));
    diag.log(`テスト走行開始（${simFactor}倍速。倍速は診断パネルで変更）`);
    return;
  }
  const res = await fetch('/sample.gpx');
  await startReplay(await res.text());
});
function toggleCamera() {
  camera.setMode(camera.mode === 'chase' ? 'overview' : 'chase');
  if (lastSample) nav.update(lastSample);
}
$('btn-camera').addEventListener('click', toggleCamera);

// 方位磁針: 地図の向きに合わせて北を指す。タップで追従（進行方向が上）／俯瞰（北が上）を切り替える
$('compass').innerHTML = COMPASS;
const needle = $('compass').querySelector('svg')!;
map.on('rotate', () => (needle.style.transform = `rotate(${-map.getBearing()}deg)`));
$('compass').addEventListener('click', toggleCamera);
$('btn-3d').addEventListener('click', () => void toggle3D());
$('btn-clear').addEventListener('click', () => {
  closeRoutes();
  goPopup?.remove();
  candidate = null;
  pending = null;
  destination = null;
  destPin.set(null);
  applyRoute(null);
});
$('routes-start').addEventListener('click', startGuidance);
$('routes-cancel').addEventListener('click', cancelRoutes);
$('btn-diag').addEventListener('click', () => diag.toggle());
$<HTMLInputElement>('gpx-file').addEventListener('change', async (e) => {
  const f = (e.target as HTMLInputElement).files?.[0];
  if (f) await startReplay(await f.text()).catch((err) => toast(String(err.message ?? err)));
});

/* ------------------------------------------------------------------ */
/* 地図の長押しで「ここに行く」／目的地のピンをずらす                      */
/* ------------------------------------------------------------------ */

let goPopup: Popup | null = null;
let longPressAt = 0;
// 地図をタップしたら閉じる。ただし長押しで指を離したときのタップでは閉じない（出した直後に消えてしまうため）
map.on('click', () => {
  if (Date.now() - longPressAt > 800) goPopup?.remove();
});
onLongPress(map, (p) => {
  longPressAt = Date.now();
  showGoPopup(p);
});

/** 「ここに行く」のポップアップ（長押しした地点、または目的地のピンをタップしたとき） */
function showGoPopup(p: LngLat) {
  goPopup?.remove();
  const el = document.createElement('div');
  el.className = 'goto';
  const btn = document.createElement('button');
  btn.className = 'btn primary small';
  btn.type = 'button';
  btn.textContent = 'ここに行く';
  btn.addEventListener('click', () => {
    goPopup?.remove();
    void goTo(p);
  });
  el.append(btn);
  goPopup = new Popup({ closeButton: true, closeOnClick: false, offset: 12, className: 'goto-popup' }).setLngLat(p).setDOMContent(el).addTo(map);
}

/**
 * 検索で選んだ目的地の候補。ピンを立てるだけで、ルートはまだ探さない。
 * ピンはドラッグでずらせて、タップすると「ここに行く」が出る
 */
let candidate: LngLat | null = null;

function placeCandidate(p: LngLat) {
  if (!$('routes').hidden) cancelRoutes();
  goPopup?.remove();
  candidate = p;
  destPin.set(p);
  destPin.setDraggable(true);
  camera.setMode('free');
  map.setPadding({ top: 0, bottom: 0, left: 0, right: 0 });
  map.easeTo({ center: p, zoom: Math.max(map.getZoom(), 15), pitch: 0, bearing: 0, duration: 800 });
  toast('ピンをタップして「ここに行く」を選ぶとルートを探します（ピンはドラッグでずらせます）');
}

destPin.onClick(() => {
  const p = candidate ?? pending;
  if (p && !destPin.isDragging) showGoPopup(p);
});

/**
 * 高速道路の上なら確認する。「いいえ」なら近くの一般道（側道など）にずらす。
 * 目的地にできないときは null
 */
async function checkHighway(p: LngLat): Promise<LngLat | null> {
  if (!isOnHighway(map, p)) return p;
  if (await confirmDialog('高速道路上ですがよろしいですか？', { note: '「いいえ」を選ぶと、近くの一般道（側道など）に目的地をずらします' })) return p;
  const q = nearestLocalRoad(map, p);
  if (!q) {
    toast('近くに一般道が見つかりませんでした。地図を拡大して、一般道の上を長押ししてください');
    return null;
  }
  toast('近くの一般道に目的地をずらしました');
  return q;
}

async function goTo(p: LngLat) {
  const q = await checkHighway(p);
  if (!q) return;
  candidate = null;
  await setDestination(q);
}

// 目的地のピンを指でずらしたとき。候補の段階ならピンを動かすだけ、ルートを選んでいる間ならルートを探し直す
destPin.onDragEnd(async (p) => {
  goPopup?.remove();
  if (candidate) {
    candidate = p;
    return;
  }
  const q = await checkHighway(p);
  if (q) await setDestination(q);
  else destPin.set(pending);
});

// 住所検索
const input = $<HTMLInputElement>('search-input');
const results = $<HTMLUListElement>('search-results');
// 検索欄の × ボタン: 文字か検索結果があるときだけ出し、押すと両方消す（案内中の目的地やピンはそのまま）
const clearBtn = $<HTMLButtonElement>('search-clear');
const syncClear = () => (clearBtn.hidden = !input.value && results.hidden);
input.addEventListener('input', syncClear);
clearBtn.addEventListener('click', () => {
  input.value = '';
  results.innerHTML = '';
  results.hidden = true;
  syncClear();
});
$('search').addEventListener('submit', async (e) => {
  e.preventDefault();
  const q = input.value.trim();
  if (!q) return;
  input.blur();
  try {
    const res = await geocode(q, map.getCenter().toArray() as LngLat);
    results.innerHTML = '';
    results.hidden = res.results.length === 0;
    if (!res.results.length) toast('見つかりませんでした');
    for (const r of res.results) {
      const li = document.createElement('li');
      li.textContent = r.label;
      li.addEventListener('click', () => {
        results.hidden = true;
        input.value = r.label;
        syncClear();
        placeCandidate(r.position);
      });
      results.append(li);
    }
    // 検索結果の出典（OpenStreetMap のデータは帰属表示が必要）
    if (res.results.length && res.attribution) {
      const src = document.createElement('li');
      src.className = 'search-attr';
      src.textContent = `出典: ${res.attribution}`;
      results.append(src);
    }
  } catch (err) {
    toast(`検索に失敗しました: ${err instanceof Error ? err.message : err}`);
  }
  syncClear();
});

map.on('load', () => {
  routeLayer.install();
  const gl = map.getCanvas().getContext('webgl2');
  const dbg = gl?.getExtension('WEBGL_debug_renderer_info');
  diag.set('gl', dbg ? String(gl!.getParameter(dbg.UNMASKED_RENDERER_WEBGL)) : gl ? 'WebGL2' : '—');
});

// 最初のタップ（同意ボタン）で、ユーザー操作が必要な許可をまとめて取る
showDisclaimer(async () => {
  speaker.unlock();
  diag.set('voice', `${speaker.supported ? 'OK' : '非対応'} / ${speaker.voiceName}`);
  await Promise.all([wake.enable(), compass.start()]);
  diag.set('compass', compass.status);
  // 起動したらすぐ現在地を表示する（「GPS」を押したのと同じ）。拒否されているときは何もしない
  if (!source && (await geolocationPermission()) !== 'denied') startSource(new GeolocationSource());
});

updateButtons();
$('version').textContent = __APP_VERSION__;

// PWA: Service Worker（本番ビルドのみ）
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  navigator.serviceWorker.register('/sw.js').catch((e) => console.warn('[sw]', e));
}
